import { useMemo, useRef, useState } from "react";

import { isPast } from "date-fns";

import { SelectWithValidation as StyledSelect } from "@/views/reports/SelectWithValidation";
import Button from "@components/Button";
import { Checkbox, Input, Select } from "@components/Field";
import Modal, {
  ModalContent,
  ModalFooter,
  ModalTitle,
} from "@components/Modal";
import { i18n } from "@lingui/core";

import { widgetTitle } from "../widgets/helpers";

import styles from "./EditWidgetModal.module.css";

const metricTitleMapping = {
  num_mentions: L.REPORTS_WIDGET_METRIC_TYPE_NUM_MENTIONS,
  pr_value: L.REPORTS_WIDGET_METRIC_TYPE_PR_VALUE,
  reach: L.REPORTS_WIDGET_METRIC_TYPE_REACH,
  sentiment: L.REPORTS_WIDGET_METRIC_TYPE_TONALITY,
  top_channels: L.REPORTS_WIDGET_METRIC_TYPE_TOP_CHANNELS,
  topics: L.REPORTS_WIDGET_METRIC_TYPE_TOPICS,
  media_types: L.REPORTS_WIDGET_METRIC_TYPE_MEDIA_TYPES,
  mentions: L.REPORTS_WIDGET_METRIC_TYPE_MENTIONS,
  engagement: L.REPORTS_WIDGET_METRIC_TYPE_ENGAGEMENT,
  popularity_score: L.REPORTS_WIDGET_METRIC_TYPE_POPULARITY_SCORE,
  word_cloud: L.REPORTS_WIDGET_METRIC_TYPE_WORD_CLOUD,
};

const chartTypeTitleMapping = {
  line: L.REPORTS_WIDGET_CHART_TYPE_LINE,
  pie: L.REPORTS_WIDGET_CHART_TYPE_CIRCLE,
  area_stacked: L.REPORTS_WIDGET_CHART_TYPE_AREA_STACKED,
  bar: L.REPORTS_WIDGET_CHART_TYPE_BAR,
  sum: L.REPORTS_WIDGET_CHART_TYPE_SUM,
};

const metricsWithoutChartType = [
  "top_channels",
  "topics",
  "mentions",
  "word_cloud",
];

const metricsWithSumType = [
  "num_mentions",
  "pr_value",
  "reach",
  "engagement",
  "popularity_score",
];

const EditWidgetModal = (props) => {
  const defaultState = useMemo(
    () => ({
      uuid: "",
      title: "",
      metric: "num_mentions",
      chart_type: "line",
      searches: [],
      tags: [],
      sorting_position: 10,
      is_compact: false,
    }),
    []
  );
  const formRef = useRef(null);
  const [uuid, setUuid] = useState(defaultState.uuid);
  const [title, setTitle] = useState(defaultState.title);
  const [metric, setMetric] = useState(defaultState.metric);
  const [chartType, setChartType] = useState(defaultState.chart_type);
  const [alerts, setAlerts] = useState(defaultState.searches);
  const [tags, setTags] = useState(defaultState.tags);
  const [sortingPosition, setSortingPosition] = useState(
    defaultState.sorting_position
  );
  const [isCompact, setIsCompact] = useState(defaultState.is_compact);

  const [, setValidated] = useState(false);
  const [errors, setErrors] = useState({});
  // Seeded, not "", because the sync below only fires once metric/chartType
  // *change* — otherwise the field opens with no placeholder.
  const [titlePlaceholder, setTitlePlaceholder] = useState(() =>
    widgetTitle({ chartType, metric })
  );

  const setStateFromObject = (object) => {
    setUuid(object.uuid);
    setTitle(object.title);
    setMetric(object.metric);
    setChartType(object.chart_type);
    setAlerts(object.searches);
    setTags(object.tags);
    setSortingPosition(object.sorting_position);
    setIsCompact(object.is_compact);
  };

  // Side-effects: sync the form state from the incoming props.
  const [prevDefaultState, setPrevDefaultState] = useState(defaultState);
  const [prevEditMode, setPrevEditMode] = useState(props.editMode);
  const [prevWidgetToEdit, setPrevWidgetToEdit] = useState(props.widgetToEdit);
  if (
    defaultState !== prevDefaultState ||
    props.editMode !== prevEditMode ||
    props.widgetToEdit !== prevWidgetToEdit
  ) {
    setPrevDefaultState(defaultState);
    setPrevEditMode(props.editMode);
    setPrevWidgetToEdit(props.widgetToEdit);

    const editMode = props.editMode;
    if (editMode) {
      setStateFromObject(props.widgetToEdit);
      // allow 0 as sorting value
      const sort = parseInt(props.widgetToEdit.sorting_position);
      setSortingPosition(isNaN(sort) ? 10 : sort);
    } else {
      setStateFromObject(defaultState);
    }
    setValidated(false);
  }

  const constructWidgetParams = () => ({
    uuid,
    title,
    metric,
    chart_type: chartType,
    sorting_position: sortingPosition,
    searches: alerts,
    tags,
    is_compact: isCompact,
  });

  const onSelectMapper = {
    metric: setMetric,
    chartType: setChartType,
    alerts: setAlerts,
    tags: setTags,
  };

  const [prevMetric, setPrevMetric] = useState(metric);
  const [prevChartType, setPrevChartType] = useState(chartType);
  if (metric !== prevMetric || chartType !== prevChartType) {
    setPrevMetric(metric);
    setPrevChartType(chartType);
    setTitlePlaceholder(widgetTitle({ chartType, metric }));
  }

  const searchToOption = (search) => ({
    value: search.id,
    label: search.title,
    uuid: search.uuid,
    type: "search",
  });

  const savedAlertOptions = [];
  const activeAlertOptions = [];
  const expiredAlertOptions = [];

  (props.searches || []).forEach((search) => {
    // add saved mentions to its own array
    if (search.kind === "static_report") {
      savedAlertOptions.push(searchToOption(search));
      return;
    }

    // handle alerts
    if (isPast(new Date(search.end_date))) {
      expiredAlertOptions.push(searchToOption(search));
    } else {
      activeAlertOptions.push(searchToOption(search));
    }
  });

  // A bit un-DRY, but we want to place "favorites" first (as in the menu-bar)
  const regularTags = props.tags.filter((tag) => tag.name !== "favorited");
  const favoriteTag = props.tags.filter((tag) => tag.name === "favorited");

  const regularTagOptions = (regularTags || []).map((tag) => ({
    value: tag.slug,
    label: tag.name,
    type: "tag",
  }));

  const favoriteTagOption = (favoriteTag || []).map((tag) => ({
    value: tag.slug,
    label: L.REPORTS_WIDGET_SOURCES_FAVORITE_OPTION,
    type: "tag",
  }));

  const allTagOptions = favoriteTagOption.concat(regularTagOptions);

  const sourceOptions = [
    {
      label: L.REPORTS_WIDGET_SOURCES_ACTIVE_ALERTS_GROUP_HEADER,
      options: activeAlertOptions,
    },
    {
      label: L.REPORTS_WIDGET_SOURCES_EXPIRED_ALERTS_GROUP_HEADER,
      options: expiredAlertOptions,
    },
    {
      label: L.REPORTS_WIDGET_SOURCES_TAGS_GROUP_HEADER,
      options: allTagOptions,
    },
    {
      label: L.REPORTS_WIDGET_SOURCES_SAVED_MENTIONS_GROUP_HEADER,
      options: savedAlertOptions,
    },
  ];

  const handleHide = () => {
    props.onBackButtonClick();
    props.setWidgetToEdit(null);
  };

  const handleDelete = () => {
    props.onDeleteWidget(props.widgetToEdit);
    handleHide();
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const validateForm = (form) => {
    setValidated(true);
    let valid = form.checkValidity();

    if (alerts.length == 0 && tags.length == 0) {
      document.getElementById("sourceSelect").classList.add("is-invalid");
      valid = false;
      setErrors({
        ...errors,
        ...{
          sources: L.REPORTS_WIDGET_ERROR_MENTIONS_WIDGET_NO_SOURCES,
        },
      });
    }

    if (metric === "mentions") {
      // The mention widget is a bit special since it calculates its stats
      // using multiple searches directly in the run_search call
      if (alerts.length > 0 && tags.length > 0) {
        // The mention widget allows *either* searches or one tag
        document.getElementById("sourceSelect").classList.add("is-invalid");
        setErrors({
          ...errors,
          ...{
            sources:
              L.REPORTS_WIDGET_ERROR_MENTIONS_WIDGET_MULTIPLE_SOURCE_TYPES,
          },
        });
        valid = false;
      } else if (tags.length > 1) {
        // Only allow one tag at a time since tags for widgets works like "searches"
        document.getElementById("sourceSelect").classList.add("is-invalid");
        setErrors({
          ...errors,
          ...{
            sources: L.REPORTS_WIDGET_ERROR_MENTIONS_WIDGET_MULTIPLE_TAGS,
          },
        });
        valid = false;
      }
    }
    if (valid) {
      document.getElementById("sourceSelect").classList.add("is-valid");
      setErrors({});
    }
    return valid;
  };

  const handleSave = (form) => {
    if (validateForm(form)) {
      if (props.editMode) {
        props.onSaveEditedWidget(constructWidgetParams());
        props.setWidgetToEdit(null);
      } else {
        props.onSaveWidgetButtonClick(constructWidgetParams());
        setStateFromObject(defaultState);
      }
    }
  };

  return (
    <Modal show={props.show} onHide={handleHide}>
      <form noValidate onSubmit={handleSubmit} ref={formRef}>
        <ModalTitle>
          {props.editMode
            ? L.REPORTS_WIDGET_EDIT_TITLE
            : L.REPORTS_WIDGET_NEW_TITLE}
        </ModalTitle>

        <ModalContent className="flex flex-col gap-4">
          <div className="flex flex-col">
            <label htmlFor="metric">{L.REPORTS_WIDGET_METRIC_LABEL}</label>

            <div>
              <Select
                block
                id="metric"
                onChange={(e) => onSelectMapper["metric"](e.target.value)}
                value={metric}
                options={Object.entries(metricTitleMapping)
                  .filter(([value]) => {
                    if (
                      value === "popularity_score" &&
                      !props.organizationMarkets?.includes("us")
                    ) {
                      return false;
                    }

                    return true;
                  })
                  .map(([value, label]) => ({
                    value: value,
                    label: i18n._(label),
                    "data-testid": value,
                  }))}
              />
            </div>
          </div>

          <div
            className={`flex flex-col ${metricsWithoutChartType.includes(metric) ? "hidden" : ""}`}
          >
            <label>{L.REPORTS_WIDGET_CHART_TYPE_LABEL}</label>

            <div>
              <Select
                block
                id="chartType"
                onChange={(e) => onSelectMapper["chartType"](e.target.value)}
                value={chartType}
                data-testid="chart-type"
                options={Object.entries(chartTypeTitleMapping)
                  .filter(([value]) => {
                    if (
                      (["sentiment", "engagement"].includes(metric) &&
                        ["bar", "area_stacked"].includes(value)) ||
                      (value == "sum" && !metricsWithSumType.includes(metric))
                    ) {
                      return false;
                    }

                    return true;
                  })
                  .map(([value, label]) => ({
                    value,
                    label: i18n._(label),
                  }))}
              >
                {}
              </Select>
            </div>
          </div>

          <div className="flex flex-col">
            <label>{L.REPORTS_WIDGET_ALERTS_LABEL}</label>

            <div>
              <StyledSelect
                isMulti
                isClearable={false}
                id="sourceSelect"
                value={(
                  alerts.map((a) => ({
                    label: a.title,
                    value: a.id,
                    uuid: a.uuid,
                    type: "search",
                  })) || []
                ).concat(
                  tags.map((a) => ({
                    label: a.name,
                    value: a.slug,
                    type: "tag",
                  }))
                )}
                onChange={(selectedOptions) => {
                  if (!selectedOptions) {
                    setAlerts([]);
                    setTags([]);
                    return;
                  }

                  let options;
                  if (!Array.isArray(selectedOptions)) {
                    options = [selectedOptions];
                  } else {
                    options = selectedOptions;
                  }

                  setAlerts(
                    options.length
                      ? options
                          .filter((o) => o["type"] === "search")
                          .map((o) => ({
                            id: parseInt(o.value),
                            uuid: o.uuid,
                            title: o.label,
                          }))
                      : []
                  );

                  setTags(
                    options.length
                      ? options
                          .filter((o) => o["type"] === "tag")
                          .map((o) => ({
                            slug: o.value,
                            name: o.label,
                          }))
                      : []
                  );
                }}
                options={sourceOptions}
              />
              <small className="text-muted-foreground">
                {L.REPORTS_WIDGET_SOURCES_HELP_TEXT}
              </small>
              {errors.sources && (
                <div className="invalid-feedback block">{errors.sources}</div>
              )}
            </div>
          </div>

          <div className="flex flex-col">
            <label>{L.REPORTS_WIDGET_TITLE_LABEL}</label>

            <div>
              <Input
                block
                type="text"
                placeholder={titlePlaceholder}
                onChange={(e) => setTitle(e.target.value)}
                value={title || ""}
              />
            </div>
          </div>

          {chartType == "sum" && (
            <div className="flex flex-col gap-1">
              <label>{L.REPORTS_WIDGET_IS_COMPACT_LABEL}</label>

              <Checkbox
                onChange={(e) => {
                  setIsCompact(e.target.checked);
                }}
                checked={isCompact}
              />
              <small>{L.REPORTS_WIDGET_IS_COMPACT_HELP_TEXT}</small>
            </div>
          )}

          <div className="flex flex-col">
            <label>{L.REPORTS_WIDGET_SORTING_POSITION_LABEL}</label>

            <Input
              block
              required
              type="number"
              pattern="\d+"
              placeholder={L.REPORTS_WIDGET_SORTING_POSITION_LABEL}
              onChange={(e) => setSortingPosition(e.target.value)}
              value={sortingPosition}
            />
            <small className="text-muted-foreground">
              {L.REPORTS_WIDGET_SORTING_POSITION_HELP_TEXT}
            </small>
          </div>
        </ModalContent>

        <ModalFooter>
          {props.editMode && (
            <Button
              className={styles.split}
              variant="secondary"
              onClick={handleDelete}
            >
              {L.REPORTS_WIDGET_DELETE}
            </Button>
          )}

          <Button variant="secondary" onClick={handleHide}>
            {L.REPORTS_WIDGET_CANCEL}
          </Button>

          <Button onClick={() => handleSave(formRef.current)}>
            {L.REPORTS_WIDGET_SAVE}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
};

export { EditWidgetModal };
